import classNames from 'classnames';

import Icons from 'assets/icons';

import NavButton from 'containers/user/home/components/shared/navigation-button';
import { useCurrentUser } from 'state/current-user/current-user.thunk';

export interface ReportTileData {
  id: string | number;
  title: string;
  description?: string;
  hasDataIssue?: boolean;
  productType?: string;
  powerBiId?: string;
  powerBiReportType?: string;
  [key: string]: unknown;
}

interface ReportTileProps {
  report: ReportTileData;
  navigateToReport: () => void;
  isOpen: boolean;
}

const ReportTile = ({
  report,
  navigateToReport,
  isOpen,
}: ReportTileProps): React.JSX.Element => {
  const { title, description, hasDataIssue } = report;
  const currentUser = useCurrentUser();
  const isUnavailable =
    !!hasDataIssue &&
    !(currentUser?.data as { internalUser?: boolean } | undefined)?.internalUser;

  const tabIndex = isOpen ? 0 : -1;

  const tileClassName = classNames(
    'w-full h-full flex flex-col text-left border border-navy-200 rounded-md p-5 transition-all duration-200',
    { 'hover:shadow-sm': !isUnavailable },
  );

  const titleContent = (
    <>
      <div
        className={classNames('font-semibold mb-1', {
          'text-gray-500': isUnavailable,
          'text-gray-700': !isUnavailable,
        })}
      >
        {title}
      </div>

      <p
        className={classNames('text-sm mb-6', {
          'text-gray-500': isUnavailable,
          'text-gray-600': !isUnavailable,
        })}
      >
        {description}
      </p>
    </>
  );

  if (isUnavailable) {
    return (
      <div className={tileClassName}>
        {titleContent}

        <div className="mt-auto">
          <div className="flex items-center text-critical-600 mb-2">
            <Icons.Warning className="mr-2" />
            <span className="text-sm font-bold">Report Unavailable</span>
          </div>

          <NavButton
            text="Contact Client Success"
            link="mailto:clientsupport@marketviewedu.com"
          />
        </div>
      </div>
    );
  }

  return (
    <button
      className={tileClassName}
      onClick={navigateToReport}
      tabIndex={tabIndex}
    >
      {titleContent}
    </button>
  );
};

export default ReportTile;
